import React from 'react';
import {StyleSheet, View} from 'react-native';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import {Bone} from './primitives/Bone';
import {BoneBadge} from './primitives/BoneBadge';
import {BoneButton} from './primitives/BoneButton';
import {BoneDivider} from './primitives/BoneDivider';
import {BoneIcon} from './primitives/BoneIcon';
import {BoneImage} from './primitives/BoneImage';
import {BoneRow} from './primitives/BoneRow';
import {BoneStepper} from './primitives/BoneStepper';
import {BoneText} from './primitives/BoneText';

const meta = {
  title: 'Components/Primitives/Skeleton Bones',
  component: Bone,
  decorators: [
    Story => (
      <View style={styles.container}>
        <Story />
      </View>
    ),
  ],
} satisfies Meta<typeof Bone>;

export default meta;

type Story = StoryObj<typeof meta>;

export const CoreBone: Story = {
  render: () => (
    <View style={styles.section}>
      <Bone width={200} height={20} />
      <Bone width={150} height={20} borderRadius={10} />
      <Bone width="80%" height={16} />
      <Bone width={100} height={40} borderRadius={0} />
    </View>
  ),
};

export const TextVariants: Story = {
  render: () => (
    <View style={styles.section}>
      <BoneText variant="h1" />
      <BoneText variant="body" />
      <BoneText variant="caption" />
      <BoneText variant="price" />
      <BoneText variant="h1" width="50%" />
    </View>
  ),
};

export const ImageVariants: Story = {
  render: () => (
    <View style={styles.row}>
      <BoneImage variant="product" />
      <BoneImage variant="thumbnail" />
      <View style={{width: 200}}>
        <BoneImage variant="banner" />
      </View>
    </View>
  ),
};

export const BadgeAndButton: Story = {
  render: () => (
    <View style={styles.section}>
      <BoneBadge />
      <BoneButton />
      <BoneButton variant="large" />
    </View>
  ),
};

export const IconSizes: Story = {
  render: () => (
    <View style={styles.row}>
      <BoneIcon size={16} />
      <BoneIcon />
      <BoneIcon size={32} />
      <BoneIcon size={48} />
    </View>
  ),
};

export const CompositeElements: Story = {
  render: () => (
    <View style={styles.section}>
      <BoneStepper />
      <BoneDivider />
      <BoneRow />
      <BoneRow leftWidth={0.6} rightWidth={0.25} height={16} />
    </View>
  ),
};

export const StaggeredAnimation: Story = {
  render: () => (
    <View style={styles.section}>
      <BoneText variant="h1" animationDelay={0} />
      <BoneText variant="body" width="80%" animationDelay={50} />
      <BoneText variant="body" width="60%" animationDelay={100} />
      <BoneText variant="caption" animationDelay={150} />
      <BoneButton animationDelay={200} />
    </View>
  ),
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    gap: 24,
  },
  section: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});
